<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:header>
      <div>
        <v-card-title class="screen-title"> ポリシー編集 </v-card-title>
        <v-card-subtitle class="screen-sub-title">
          NAT(IP Masquerade) アクセス拠点 → インターネット
        </v-card-subtitle>
      </div>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="closeModal"
      >
        ×
      </button>
    </template>
    <template v-if="isLoaded" v-slot:body>
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <v-row
              no-gutters
              justify="space-between"
              class="my-2"
              align="center"
            >
              <span>{{ table.items.length }}件を表示</span>
              <v-btn color="primary" @click="addPolicy" :disabled="limitPolicy"
                >ルール追加
              </v-btn>
            </v-row>
            <app-draggable-table
              density="compact"
              :fields="table.fields"
              :items="table.items"
              primary-key="policyId"
              :hover="true"
              @click:row="editPolicy"
              sticky-header="500px"
              :td-class="tdClass"
              :tr-class="policyRowClass"
            >
              <!-- 送信元 -->
              <template #cell(srcAddressList)="{ value }">
                <policy-src-address :src-address="{ srcAddressList: value }" />
              </template>

              <!-- 宛先 -->
              <template #cell(dstAddressList)="{ value }">
                <policy-dst-address :dst-address="{ dstAddressList: value }" />
              </template>

              <!-- IP Pool -->
              <template #cell(ipPoolList)="{ value }">
                <policy-ip-pool :ip-pool-list="value" />
              </template>

              <!-- 有効/有無 -->
              <template #cell(isPolicyStatus)="{ value }">
                {{ $filter.enumTo(value, "enable") }}
              </template>

              <!-- 備考 -->
              <template #cell(description)="{ value }">
                {{ $filter.emptyTo(value, "-") }}
              </template>

              <!-- 削除 -->
              <template #cell(delete)="{ item }">
                <v-btn
                  v-if="item.policyCategoryInfo !== 'UNEDITABLE_DEFAULT_POLICY'"
                  color="primary"
                  density="comfortable"
                  @click.stop="deletePolicy(item)"
                  >削除
                </v-btn>
              </template>
            </app-draggable-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="flat" color="primary" @click="submit">保存</v-btn>
            <v-btn variant="outlined" color="primary" @click="closeModal"
              >キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { GetPolicy, GetPolicyType4 } from "@/apis/NwPolicyApi";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import PolicyDstAddress from "@/components/policy/PolicyDstAddress.vue";
import PolicySrcAddress from "@/components/policy/PolicySrcAddress.vue";
import PolicyIpPool from "@/components/policy/PolicyIpPool.vue";
import type { Type4Service } from "@/apis/ContractChangeType4Api";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import { VueFinalModal } from "vue-final-modal";
import type { AppDraggableTableHeader } from "@/components/AppDraggableTable.vue";
import InternetType4PolicyRuleIpMasqEdit from "@/modals/applianceContractSetting/type4/policy/ipMasquerade/InternetType4PolicyRuleIpMasqEdit.vue";

export type Type4IpMasqueradePolicy = NonNullable<
  GetPolicyType4["policyIPMasqueradeType4"]
>["policyList"][0];

export default defineComponent({
  name: "InternetType4PolicyRuleIpMasqModify",
  components: { PolicyIpPool, PolicySrcAddress, PolicyDstAddress },
  props: {
    /** Type4Id. 主キー */
    type4Id: {
      type: String as PropType<string>,
      required: true,
    },
    /** ポリシーリスト */
    policyList: {
      type: Array as PropType<Type4IpMasqueradePolicy[]>,
      required: true,
    },
    /** type4ポリシー全体 */
    type4Policy: {
      type: Object as PropType<GetPolicyType4>,
      required: true,
    },
    /** サービスメニュー(インターネットType1) */
    type4Service: {
      type: Object as PropType<Type4Service>,
      required: true,
    },
  },
  data() {
    return {
      table: {
        fields: [
          { key: "_handle", title: "↑↓", width: "50px", align: "center" },
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddressList", title: "送信元アドレス", width: "150px" },
          { key: "dstAddressList", title: "宛先アドレス", width: "150px" },
          { key: "ipPoolList", title: "IP Pool", width: "150px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考" },
          { key: "delete", title: "", width: "50px" },
        ] as AppDraggableTableHeader<Type4IpMasqueradePolicy>[],
        items: cloneDeep(this.policyList),
      },
      natPolicyLimit: [] as UpperLimitEntity[],
      isLoaded: false,
      // 変更検知用の初期データ
      initialPolicyList: [] as Type4IpMasqueradePolicy[],
    };
  },
  computed: {
    /** 変更があるかどうかを判定 */
    hasChanges(): boolean {
      return !isEqual(this.table.items, this.initialPolicyList);
    },

    /** true: 作成できるポリシー数の上限, false: 上限ではない */
    limitPolicy(): boolean {
      // NAT側のカスタムポリシー数
      const totalPolicy =
        (this.latestType4Policy.policyIPMasqueradeType4?.policyList.filter(
          (e) => e.policyCategoryInfo === "CUSTOM_POLICY"
        ).length ?? 0) +
        (this.latestType4Policy.policyNatType4?.policyList.length ?? 0);
      return (
        totalPolicy >=
        this.natPolicyLimit!.filter(
          (e) => e.upperLimitItemName === "ポリシー数(NAT)"
        )[0]!.upperLimit
      );
    },
    /** 最新の全体ポリシー（全体ポリシー + 編集中のポリシー ）*/
    latestType4Policy(): GetPolicyType4 {
      return {
        ...this.type4Policy,
        policyIPMasqueradeType4: {
          ...this.type4Policy.policyIPMasqueradeType4!,
          policyList: this.table.items,
        },
      };
    },
  },
  async mounted() {
    // 初期データを保存
    this.initialPolicyList = cloneDeep(this.policyList);
    /** NATの設定上限値を取得 */
    this.natPolicyLimit = (
      await this.$api.upperLimit.getUpperLimitLine({
        upperLimitLineManageUnit: "APPLIANCE",
        applianceType: "NAT",
        wnumber: this.type4Service.wnumberMainAct,
        type4Id: this.type4Service.type4Id,
      })
    ).internetLine!.upperLimitList!;
    this.isLoaded = true;
  },
  methods: {
    tdClass(field: AppDraggableTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
    /** ポリシー追加 */
    async addPolicy() {
      const addPolicy: Type4IpMasqueradePolicy = await this.$modal.show(
        InternetType4PolicyRuleIpMasqEdit,
        {
          type4Id: this.type4Id,
          type4Policy: this.latestType4Policy,
        }
      );
      this.table.items.splice(this.table.items.length, 0, addPolicy);
    },

    /** ポリシー編集 */
    async editPolicy(policy: Type4IpMasqueradePolicy) {
      if (policy.policyCategoryInfo === "UNEDITABLE_DEFAULT_POLICY") {
        return;
      }
      const editPolicy: Type4IpMasqueradePolicy = await this.$modal.show(
        InternetType4PolicyRuleIpMasqEdit,
        {
          type4Id: this.type4Id,
          policy,
          type4Policy: this.type4Policy,
        }
      );
      const index = this.table.items.indexOf(policy);
      this.table.items[index] = editPolicy;
    },

    /** ポリシー削除 */
    async deletePolicy(item: Type4IpMasqueradePolicy) {
      const index = this.table.items.indexOf(item);
      this.table.items.splice(index, 1);
    },

    /** ルール編集完了 */
    async submit() {
      (
        this.$refs.modal as typeof VueFinalModal & { ok: (e: unknown) => void }
      ).ok(this.table.items);
    },

    /** ×ボタン、キャンセルボタンクリック時の処理 */
    async closeModal() {
      // 変更がない場合はそのまま閉じる
      if (!this.hasChanges) {
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
        return;
      }

      try {
        await this.$confirm("変更を破棄してキャンセルします、よろしいですか？");
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
      } catch (error) {
        // キャンセルが押された場合：何もしない
        return;
      }
    },

    /** ポリシーテーブルのtrクラス. */
    policyRowClass(
      item: Pick<GetPolicy, "policyCategoryInfo">
    ): string[] | undefined {
      switch (item.policyCategoryInfo) {
        case "CUSTOM_POLICY":
          // ドラッグ可能
          return ["draggable", "clickable"];
        case "EDITABLE_DEFAULT_POLICY":
          // ドラッグ可能
          return ["draggable", "clickable"];
        case "UNEDITABLE_DEFAULT_POLICY":
          // ドラッグ不可 + 背景色グレーアウト
          return ["gray-out"];
      }
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(th) {
  font-size: 90%;
}
</style>
